import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {CheckBox} from '../../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {DismissedAlertsEnum} from '../../../../../models/api/preferencesModel';
import {updateDismissedAlertsAction} from '../../../../../store/data/preferences/preferencesActions';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';

/*
 * Props.
 */

interface LinkedConversationAccessDialogProps {
  onOkClick: () => void;
  onRequestClose: () => void;
}

/*
 * Styles.
 */

const StyledCheckBoxContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  padding: 7px 0;
`;

/*
 * Component.
 */

export const LinkedConversationAccessDialog: FC<LinkedConversationAccessDialogProps> = (props) => {
  const [shouldNotShowAgain, setShouldNotShowAgain] = useState(false);
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const user = useAppSelector(getUser);

  return (
    <OkCancelDialog
      title={<Trans id="linked-conversation-access-dialogtitle">Conversation access</Trans>}
      okButtonLabel={<Trans id="linked-conversation-access-dialogcontinue">Continue</Trans>}
      onRequestClose={() => {
        props.onRequestClose();
        trackDataEvent(DataEventActionsEnum.CLOSE, LinkedConversationsDataEventObject.ACCESS_MODAL, {
          requestorId: user.id,
        });
      }}
      onOkClick={() => {
        if (shouldNotShowAgain) {
          dispatch(updateDismissedAlertsAction(DismissedAlertsEnum.LINKED_CONVERSATION_ACCESS));
        }
        props.onOkClick();
        trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.ACCESS_MODAL, {
          requestorId: user.id,
          wasModalDismissed: shouldNotShowAgain,
        });
      }}
    >
      <Trans id="linked-conversation-access-dialogbody">
        Participants in the original conversation will have access to the linked conversation, and vice versa.
      </Trans>

      <StyledCheckBoxContainerDiv>
        <CheckBox isChecked={shouldNotShowAgain} onChange={setShouldNotShowAgain} />
        <Trans id="linked-conversation-access-dialogdontshow">Don&apos;t show me this again</Trans>
      </StyledCheckBoxContainerDiv>
    </OkCancelDialog>
  );
};
